import { SIDE_PANEL_WIDTH } from '@/constants/side-panel'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomerEventWarning, WarningItem } from '@/types/warnings'
import {
    AppPanel,
    AppPanelBody,
    AppPanelHeader,
} from '@vnetwork/datacentral-ui/layout'
import {
    Badge,
    Button,
    Disclosure,
    IconButton,
    SegmentedControl,
    Text,
} from '@vnetwork/datacentral-ui/primitives'
import { Alert, Loading } from '@vnetwork/datacentral-ui/feedback'
import { EmptyState } from '@vnetwork/datacentral-ui/data'
import {
    AlertTriangle,
    ArrowUpCircle,
    Cake,
    CircleCheck,
    Clock,
    FileClock,
    FileText,
    Hourglass,
    Inbox,
    OctagonAlert,
    X,
} from 'lucide-react'
import AlertDetail from './AlertDetail'
import {
    LEAD_GROUP_FALLBACK_TITLE,
    useAlertNotifications,
} from './use-alert-notifications'

// Groups can hold hundreds of items; render a page at a time.
const GROUP_PAGE_SIZE = 20

/** System warnings docked beside the page (DashboardLayout `panel`). */
const AlertPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const { t } = useTranslation()
    const {
        isLoading,
        isError,
        failedSources,
        groupedAlerts,
        totalCount,
        highPriorityCount,
    } = useAlertNotifications()
    const [severity, setSeverity] = useState<'all' | 'high'>('all')
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
    const [shown, setShown] = useState<Record<string, number>>({})

    const getAlertIcon = (type: WarningItem['type']) => {
        switch (type) {
            case 'contract_expired':
                return <OctagonAlert size={16} />
            case 'contract_expiring':
                return <FileClock size={16} />
            case 'dayoff_pending':
                return <Hourglass size={16} />
            case 'document_retrieval_upcoming':
            case 'document_retrieval_overdue':
                return <FileText size={16} />
            case 'lead_unassigned':
                return <Inbox size={16} />
            case 'lead_stale':
                return <Clock size={16} />
            case 'ticket_escalated':
                return <ArrowUpCircle size={16} />
            case 'customer_event_upcoming':
                return <Cake size={16} />
            default:
                return <AlertTriangle size={16} />
        }
    }

    const setGroupOpen = (type: string, open: boolean) =>
        setExpandedGroups((prev) => {
            const next = new Set(prev)
            if (open) next.add(type)
            else next.delete(type)
            return next
        })

    const getAlertKey = (alert: WarningItem) => {
        if (alert.type === 'customer_event_upcoming') {
            const event = alert.data as CustomerEventWarning
            return (
                event.event_key ||
                `${alert.type}-${event.customer_id}-${event.event_type}-${event.event_month}-${event.event_day}`
            )
        }
        return `${alert.type}-${alert.id}`
    }

    const groupTone = (type: string) =>
        type === 'contract_expired'
            ? 'bg-danger-subtle text-danger-text'
            : type === 'contract_expiring'
              ? 'bg-warning-subtle text-warning-text'
              : 'bg-primary-subtle text-primary-text'

    // "Khẩn cấp" keeps only high-severity items and drops groups left empty.
    const visibleGroups = Object.entries(groupedAlerts)
        .map(
            ([type, items]) =>
                [
                    type,
                    severity === 'high'
                        ? items.filter((item) => item.severity === 'high')
                        : items,
                ] as const
        )
        .filter(([, items]) => severity === 'all' || items.length > 0)

    const groupTitle = (type: string, items: WarningItem[]) =>
        type === 'customer_event_upcoming'
            ? t('customer:calendar_events.alert_title')
            : t(`alerts.type.${type}`, {
                  defaultValue:
                      items[0]?.title ??
                      (LEAD_GROUP_FALLBACK_TITLE[type]
                          ? t(LEAD_GROUP_FALLBACK_TITLE[type])
                          : type),
              })

    return (
        <AppPanel width={SIDE_PANEL_WIDTH} aria-label={t('alert-notification')}>
            <AppPanelHeader
                className="!h-[var(--vn-chrome-row-height)]"
                actions={
                    <IconButton
                        aria-label={t('close')}
                        size="sm"
                        onClick={onClose}
                    >
                        <X size={16} />
                    </IconButton>
                }
            >
                <span className="w-8 h-8 shrink-0 bg-danger-subtle text-danger-text rounded-full flex items-center justify-center">
                    <AlertTriangle size={18} />
                </span>
                <div className="min-w-0">
                    <Text as="div" weight="semibold" truncate>
                        {t('alert-notification')}
                    </Text>
                    <Text as="div" size="sm" tone="muted" truncate>
                        {t('alert-notification-count', { totalCount })}
                    </Text>
                </div>
            </AppPanelHeader>
            {highPriorityCount > 0 && (
                <div className="border-b border-border px-4 py-2">
                    <SegmentedControl
                        size="sm"
                        aria-label={t('alerts.severity.high')}
                        value={severity}
                        onValueChange={(v) => setSeverity(v as 'all' | 'high')}
                        items={[
                            {
                                value: 'all',
                                label: `${t('all')} (${totalCount})`,
                            },
                            {
                                value: 'high',
                                label: `${t('alerts.severity.high')} (${highPriorityCount})`,
                            },
                        ]}
                    />
                </div>
            )}
            <AppPanelBody className="p-0">
                {isLoading ? (
                    <Loading className="py-8" />
                ) : isError ? (
                    <div className="p-4">
                        <Alert tone="danger">
                            {t('customer:calendar_events.alert_load_error')}
                        </Alert>
                    </div>
                ) : (
                    <>
                        {failedSources.length > 0 && (
                            <div className="p-4 pb-0">
                                <Alert tone="warning">
                                    {t(
                                        'customer:calendar_events.alert_partial',
                                        {
                                            count: failedSources.length,
                                        }
                                    )}
                                </Alert>
                            </div>
                        )}
                        {visibleGroups.length === 0 ? (
                            failedSources.length > 0 ? (
                                <EmptyState
                                    size="sm"
                                    title={t(
                                        'customer:calendar_events.alert_partial_empty'
                                    )}
                                />
                            ) : (
                                <EmptyState
                                    size="sm"
                                    icon={
                                        <CircleCheck className="text-success-text" />
                                    }
                                    title={t('no-alert-to-process')}
                                />
                            )
                        ) : (
                            visibleGroups.map(([type, items]) => {
                                const limit = shown[type] ?? GROUP_PAGE_SIZE
                                return (
                                    <div
                                        key={type}
                                        className="border-b border-border last:border-b-0"
                                    >
                                        <Disclosure
                                            className="[&>button]:w-full [&>button]:px-4 [&>button]:py-2.5 [&>button]:gap-3 [&>button]:text-left"
                                            open={expandedGroups.has(type)}
                                            onOpenChange={(open) =>
                                                setGroupOpen(type, open)
                                            }
                                            summary={
                                                <span className="flex min-w-0 flex-1 items-center gap-2">
                                                    <span
                                                        className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${groupTone(type)}`}
                                                    >
                                                        {getAlertIcon(
                                                            type as WarningItem['type']
                                                        )}
                                                    </span>
                                                    <Text
                                                        as="span"
                                                        weight="medium"
                                                        className="min-w-0 flex-1 text-left first-letter:uppercase"
                                                    >
                                                        {groupTitle(
                                                            type,
                                                            items
                                                        )}
                                                    </Text>
                                                    <Badge
                                                        size="sm"
                                                        tone="neutral"
                                                    >
                                                        {items.length}
                                                    </Badge>
                                                </span>
                                            }
                                        >
                                            <div className="flex flex-col">
                                                {items
                                                    .slice(0, limit)
                                                    .map((alert) => (
                                                        <AlertDetail
                                                            key={getAlertKey(
                                                                alert
                                                            )}
                                                            alert={alert}
                                                        />
                                                    ))}
                                                {items.length > limit && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="border-t border-border-subtle"
                                                        onClick={() =>
                                                            setShown(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    [type]:
                                                                        limit +
                                                                        GROUP_PAGE_SIZE,
                                                                })
                                                            )
                                                        }
                                                    >
                                                        {t('see-more')} (
                                                        {items.length - limit})
                                                    </Button>
                                                )}
                                            </div>
                                        </Disclosure>
                                    </div>
                                )
                            })
                        )}
                    </>
                )}
            </AppPanelBody>
        </AppPanel>
    )
}

export default AlertPanel
